import { useEffect, useMemo, useState } from 'react';
import { SearchIcon, PencilIcon, TrashIcon, PlusIcon, ChevronDownIcon } from '@bizfirst/common-react';
import type { AgentDTO } from '../../types';
import { useAgentAdminApiClient } from '../../context/AiAgentAdminApiContext';
import { AgentEditor } from './AgentEditor';
import { ConfirmDialog } from '../Shared/ConfirmDialog';

export interface AgentPickerProps {
  value?: number | null;
  onChange: (agentId: number | null, agent?: AgentDTO) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Show Create/Edit/Delete affordances. Defaults true — set false for a read-only picker. */
  allowManage?: boolean;
  /** Show a clear (×) button in the search box that empties the text and the selection. Defaults false. */
  clearable?: boolean;
  /** For a non-clearable picker: when the typed text is emptied, keep the selection and show its name again. Defaults false. Ignored when clearable. */
  keepSelectionWhenTextCleared?: boolean;
  /** Pass through to the editor: on narrow screens its section tabs become a hamburger side panel. Defaults false. */
  compactEditorTabs?: boolean;
}

/**
 * Search/select/create/edit/delete for Agents — generalized from flow-studio-designer's
 * ConnectorConfigDialog.tsx inline Agent-tab markup (lines 1833-1919) into a standalone,
 * host-agnostic component. Delete is new here (see AgentAdminApiClient.deleteById's comment for
 * the confirmed frontend gap this closes).
 */
export function AgentPicker({ value, onChange, label = 'Agent', placeholder = 'Search agents...', disabled, allowManage = true, clearable = false, keepSelectionWhenTextCleared = false, compactEditorTabs = false }: AgentPickerProps) {
  const api = useAgentAdminApiClient();
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [loading, setLoading] = useState(false);
  // null = the user is not typing, so the field shows the selected agent's name. Any string means
  // the user has typed (including '' after they cleared the field), so the name is not shown again.
  const [query, setQuery] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [editorState, setEditorState] = useState<{ mode: 'create' | 'edit'; agentId?: number } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AgentDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  const selectedAgent = useMemo(() => agents.find((a) => a.AgentID === value) ?? null, [agents, value]);

  const loadAgents = async (openAfter = true) => {
    setLoading(true);
    const result = await api.getActive();
    setAgents(result.data ?? []);
    setLoading(false);
    if (openAfter) setShowResults(true);
  };

  useEffect(() => {
    // If a value is preselected but not yet in the local list (e.g. deep link), load once so the
    // selected agent's name can be shown instead of a bare ID. This must NOT open the dropdown —
    // it's a silent name resolution, not a user-initiated browse.
    if (value && !agents.some((a) => a.AgentID === value)) {
      loadAgents(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const keepSelectionOnEmpty = keepSelectionWhenTextCleared && !clearable;
  const inputText = keepSelectionOnEmpty ? (query || selectedAgent?.Name || '') : (query ?? selectedAgent?.Name ?? '');
  const searchText = query?.trim() ?? '';
  const filtered = searchText
    ? agents.filter((a) => a.Name.toLowerCase().includes(searchText.toLowerCase()))
    : agents;

  const handleSelect = (agent: AgentDTO) => {
    onChange(agent.AgentID, agent);
    setQuery(null);
    setShowResults(false);
  };

  // Empties the typed text and the selection, then shows the full list so the user can pick again.
  const handleClear = () => {
    setQuery('');
    onChange(null);
    setShowResults(true);
    if (!agents.length) loadAgents();
  };

  // Clearing the text by hand (e.g. backspace to empty) also drops the selection and shows all agents,
  // unless the picker opts into keepSelectionWhenTextCleared.
  const handleTextChange = (text: string) => {
    setQuery(text);
    setShowResults(true);
    if (text === '' && value && !keepSelectionOnEmpty) onChange(null);
    if (!agents.length) loadAgents();
  };

  const handleEditorSaved = async (agent: AgentDTO) => {
    await loadAgents();
    setEditorState(null);
    onChange(agent.AgentID, agent);
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const result = await api.deleteById(pendingDelete.AgentID);
    setDeleting(false);
    if (result.success) {
      setPendingDelete(null);
      await loadAgents();
      if (value === pendingDelete.AgentID) onChange(null);
    }
  };

  return (
    <div className="aaa-entity-picker">
      <label className="aaa-entity-label">{label}</label>
      <div className="aaa-entity-search-row">
        <div className="aaa-entity-search-input-wrap">
          <SearchIcon size={14} />
          <div className="aaa-entity-search-field">
          <input
            type="text"
            placeholder={placeholder}
            value={inputText}
            disabled={disabled || loading}
            onChange={(e) => handleTextChange(e.target.value)}
            onFocus={() => {
              if (!agents.length) loadAgents();
              else setShowResults(true);
            }}
          />
          {clearable && !!inputText && (
            <button type="button" className="aaa-entity-search-clear" aria-label="Clear agent" title="Clear" onMouseDown={(e) => e.preventDefault()} onClick={handleClear}>
              ×
            </button>
          )}
          </div>
        </div>
        <button type="button" className="aaa-icon-btn aaa-entity-browse-btn" onClick={() => (showResults ? setShowResults(false) : loadAgents())} disabled={disabled} title="Browse agents">
          <ChevronDownIcon size={14} />
        </button>
        {allowManage && (
          <>
            <button
              type="button"
              className="aaa-icon-btn"
              disabled={disabled || !value}
              onClick={() => value && setEditorState({ mode: 'edit', agentId: value })}
              title="Edit agent"
            >
              <PencilIcon size={13} />
            </button>
            <button
              type="button"
              className="aaa-icon-btn aaa-icon-btn-danger"
              disabled={disabled || !value}
              onClick={() => selectedAgent && setPendingDelete(selectedAgent)}
              title="Delete agent"
            >
              <TrashIcon size={13} />
            </button>
            <button type="button" className="aaa-entity-create-btn" disabled={disabled} onClick={() => setEditorState({ mode: 'create' })} title="Add new agent">
              <PlusIcon size={13} /> Create
            </button>
          </>
        )}
      </div>

      {showResults && (
        <div className="aaa-entity-dropdown">
          {loading && <div className="aaa-entity-dropdown-empty">Loading…</div>}
          {!loading && filtered.length === 0 && <div className="aaa-entity-dropdown-empty">No agents found.</div>}
          {!loading &&
            filtered.map((agent) => (
              <button
                type="button"
                key={agent.AgentID}
                className={`aaa-entity-dropdown-item${agent.AgentID === value ? ' selected' : ''}`}
                onClick={() => handleSelect(agent)}
              >
                <span>{agent.Name}</span>
                {agent.ShortDescription && <span className="aaa-entity-dropdown-item-hint">{agent.ShortDescription}</span>}
              </button>
            ))}
        </div>
      )}

      {editorState && (
        <AgentEditor
          agentId={editorState.mode === 'edit' ? editorState.agentId : undefined}
          onClose={() => setEditorState(null)}
          onSaved={handleEditorSaved}
          compactTabs={compactEditorTabs}
        />
      )}

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Delete Agent"
        message={`Delete "${pendingDelete?.Name}"? This soft-deletes the agent — it will no longer appear in search or active lists.`}
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
